import { ApiError, authFetch } from "@/lib/api-client";
import {
  OPTIONS_POLL_INTERVAL_MS,
  OPTIONS_POLL_TIMEOUT_MS,
} from "@/lib/query-constants";

/**
 * Shape the `/{scope}/status/:correlationId` routes return for an options job.
 * `data` carries the payload the worker put on the correlation status; it is absent
 * for kinds whose result is too large to round-trip through Redis (see
 * `rereadOnComplete`).
 */
interface AmberOptionsStatus<T> {
  status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
  data?: T | null;
  error?: string;
  message?: string;
}

interface FetchAmberOptionsArgs {
  signal?: AbortSignal;
  /** Poll cadence override. Defaults to `OPTIONS_POLL_INTERVAL_MS`. */
  intervalMs?: number;
  /**
   * Treat a COMPLETED status as a signal to re-read `url` — by then a warm cache
   * hit — instead of reading the payload off the status envelope. For results too
   * large to duplicate into the correlation status value.
   */
  rereadOnComplete?: boolean;
}

export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Resolves an options endpoint that the workspace worker backs: a warm cache hit
 * answers `200 { data }` inline, a cold miss answers `202 { correlationId }` and the
 * result lands on `{statusPath}/status/:correlationId`.
 *
 * Every terminal failure throws rather than resolving empty: a FAILED job, an exhausted
 * poll budget, or a COMPLETED status carrying no payload. Any of them resolving `[]` or
 * `null` would render as an empty dropdown with no error surfaced anywhere, which is
 * exactly the regression this indirection risks.
 * Callers must pair this with `retry: false` — a thrown FAILED is a settled job, not
 * a transient 5xx worth retrying.
 */
export async function fetchAmberOptions<T>(
  url: string,
  statusPath: string,
  { signal, intervalMs = OPTIONS_POLL_INTERVAL_MS, rereadOnComplete = false }: FetchAmberOptionsArgs = {},
): Promise<T> {
  const first = await authFetch<T>(url, { signal });
  if (first.data != null) return first.data;

  if (!first.correlationId) {
    throw new ApiError(
      502,
      `${url} returned neither data nor a correlationId`,
      "MISSING_CORRELATION_ID",
    );
  }

  const startedAt = Date.now();
  for (;;) {
    const { data: status } = await authFetch<AmberOptionsStatus<T>>(
      `${statusPath}/status/${first.correlationId}`,
      { signal },
    );

    if (status?.status === "COMPLETED") {
      if (!rereadOnComplete) {
        // A COMPLETED job with no payload is a backend regression, not an empty result —
        // an empty result still arrives as `[]` or `{}`. Resolving `null as T` here would
        // hand callers a value their type forbids and surface as a blank dropdown.
        if (status.data == null) {
          throw new ApiError(
            502,
            `${url} completed without a result payload`,
            "OPTIONS_MISSING_PAYLOAD",
          );
        }
        return status.data;
      }

      const reread = await authFetch<T>(url, { signal });
      if (reread.data != null) return reread.data;
      throw new ApiError(
        502,
        `${url} completed but the cached result was already gone`,
        "OPTIONS_REREAD_EMPTY",
      );
    }

    if (status?.status === "FAILED") {
      throw new ApiError(
        500,
        status.error ?? status.message ?? `${url} failed`,
        "JOB_FAILED",
      );
    }

    // Checked after the request, not before the sleep, so a job that completes on
    // the very last allowed poll still resolves.
    if (Date.now() - startedAt > OPTIONS_POLL_TIMEOUT_MS) {
      throw new ApiError(504, `${url} timed out`, "POLL_TIMEOUT");
    }

    await abortableSleep(intervalMs, signal);
  }
}
